import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrdenComercio } from 'src/app/models/OrdenComercio';
import { ProductoOrdenComercio } from 'src/app/models/ProductoOrdenComercio';
import { VademecumOrdenComercio } from 'src/app/models/VademecumOrdenComercio';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { Location } from '@angular/common';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Producto } from 'src/app/models/Producto';
import { Vademecum } from 'src/app/models/Vademecum';
import { API_URI } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';

type Panel = 'details' | 'editVademecum' | 'editProducto';

@Component({
    selector: 'app-order-response-comercio',
    templateUrl: './order-response-comercio.component.html',
    styleUrls: ['./order-response-comercio.component.css'],
    standalone: false
})
export class OrderResponseComercioComponent implements OnInit, OnDestroy {
  private readonly _orderComercioService = inject(OrderComercioService);
  private readonly _usuarioService = inject(UsuarioService);
  private readonly _authService = inject(AuthService);
  private readonly _loadingService = inject(LoadingService);

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _location = inject(Location);

  private readonly subs = new Subscription();

  // ---- estado ----
  id = Number(this._route.snapshot.paramMap.get('id'));
  error = signal<string>('');
  loading = signal<boolean>(false);
  isFinished = signal<boolean>(false);

  // UI panel (reemplaza DOM directo)
  panel = signal<Panel>('details');
  isBlur = computed(() => this.panel() !== 'details');
  isActive = computed(() => (this.isBlur() ? 'add-blur' : ''));

  // data orden
  ordenComercio = signal<OrdenComercio | null>(null);
  productosOrdenComercio = signal<ProductoOrdenComercio[]>([]);
  vademecumsOrdenComercio = signal<VademecumOrdenComercio[]>([]);

  preview_img_avatar = signal<string>('');
  url_perfil_cliente = computed(() => {
    const oc = this.ordenComercio();
    return oc?.id_cliente ? `/perfil-cliente/${oc.id_cliente}` : '';
  });
  nombre = computed(() => this.ordenComercio()?.nombreCliente ?? '');
 
  // flags derivados
  b_envio_domicilio = computed(() => !!this.ordenComercio()?.b_envio_domicilio);
  b_pago_mercadopago = computed(() => !!this.ordenComercio()?.b_pago_mercadopago);
  b_pago_efectivo = computed(() => !!this.ordenComercio()?.b_pago_efectivo);
  x_img_receta = computed(() => this.ordenComercio()?.x_img_receta ?? '');

  // notas y envío
  x_notas = signal<string>('');
  n_precio_envio = signal<number>(1000);
  n_tiempo_envio = signal<number>(30);

  // totales
  n_total = signal<number>(0);
  n_ahorro = signal<number>(0);
 
  // listados para reemplazo
  productos = signal<Producto[]>([]);
  tempVademecumOrderComercio = signal<VademecumOrdenComercio | null>(null);
  tempProductoOrderComercio = signal<ProductoOrdenComercio | null>(null);
  
  // búsqueda (mantenemos tu esquema con BehaviorSubject + async)
  searchTermVademecums$ = new BehaviorSubject<string>('***');
  searchTermProductos$ = new BehaviorSubject<string>('***');
  vademecumsFiltrados$?: Observable<Vademecum[]>;
  productosFiltrados$?: Observable<Producto[]>;
  searchVademecums = '';
  searchProductos = '';

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    if (!this.id) {
      this._router.navigate(['/dashboard']);
      return;
    }

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this._router.navigate(['/sign-in']);
        return;
      }

      // filtro listas
      this.filterListVademecum();
      this.filterListProducto();

      // cargar orden
      this.subs.add(
        this._orderComercioService.getOrdenComercio(this.id).subscribe({
          next: (resOrden) => {
            // Si ya está respondida o si entra un cliente acá -> redirigir
            if ((resOrden?.id_estado_orden ?? 0) > 1 || this._usuarioService.usuario().id_perfil == 2) {
              this._router.navigate([`/order-confirm-comercio/${this.id}`]);
              return;
            }

            this.ordenComercio.set(resOrden);

            // avatar cliente
            const avatar = resOrden?.imgAvatarCliente
              ? `${API_URI}uploads/images/small-${resOrden.imgAvatarCliente}`
              : '';
            this.preview_img_avatar.set(avatar);

            // notas iniciales (si existieran)
            this.x_notas.set(resOrden?.x_notas ?? '');

            // cargar items + productos activos
            this.loadItemsYTotales();
          },
          error: (eC) => {
            console.error('Error al obtener la Orden de Comercio', eC);
            this.error.set('No se pudo cargar la orden.');
          },
        })
      );
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.searchTermVademecums$.complete();
    this.searchTermProductos$.complete();
  }

  private loadItemsYTotales(): void {
    // reset totales
    this.n_total.set(0);
    this.n_ahorro.set(0);

    // productos orden
    this.subs.add(
      this._orderComercioService.getProductosXOrdenComercio(this.id).subscribe({
        next: (resProduc) => {
          this.productosOrdenComercio.set(resProduc ?? []);
          this.refreshTotal();
        },
        error: (eP) => console.error('Error al obtener productos orden', eP),
      })
    );

    // vademécums orden
    this.subs.add(
      this._orderComercioService.getVademecumsXOrdenComercio(this.id).subscribe({
        next: (resVademe) => {
          this.vademecumsOrdenComercio.set(resVademe ?? []);
          this.refreshTotal();
        },
        error: (eV) => console.error('Error al obtener vademecums orden', eV),
      })
    );

    // productos activos del comercio (para reemplazo)
    const oc = this.ordenComercio();
    if (oc?.id_comercio) {
      this.subs.add(
        this._usuarioService.getProductos(oc.id_comercio, true).subscribe({
          next: (res) => this.productos.set(res ?? []),
          error: () => console.log('Error al obtener productos activos del comercio'),
        })
      );
    }
  }

  // ----------------- UI panel -----------------
  private openPanel(p: Panel): void {
    this.panel.set(p);
  }

  closeModal(): void {
    this.panel.set('details');
    this.tempVademecumOrderComercio.set(null);
    this.tempProductoOrderComercio.set(null);
    this.isActive; // computed, no set
  }

  changeVademecum(id_vademecum: number): void {
    const temp = this.vademecumsOrdenComercio().find(x => x.id_vademecum === id_vademecum) ?? null;
    this.tempVademecumOrderComercio.set(temp);
    this.openPanel('editVademecum');
  }

  changeProducto(id_producto: number): void {
    const temp = this.productosOrdenComercio().find(x => x.id_producto === id_producto) ?? null;
    this.tempProductoOrderComercio.set(temp);
    this.openPanel('editProducto');
  }

  // ----------------- Filtros -----------------
  filterListVademecum(): void {
    this.vademecumsFiltrados$ = this.searchTermVademecums$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      map((term) => {
        const t = (term ?? '').trim().toLowerCase();
        if (t.length <= 3) return [];
        return (this._orderComercioService.vademecums ?? [])
          .filter(item => (item.x_nombre ?? '').toLowerCase().includes(t))
          .slice(0, 3);
      })
    );
  }

  filterListProducto(): void {
    this.productosFiltrados$ = this.searchTermProductos$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      map((term) => {
        const t = (term ?? '').trim().toLowerCase();
        if (t.length <= 3) return [];
        return (this.productos() ?? [])
          .filter(item => (item.x_nombre ?? '').toLowerCase().includes(t))
          .slice(0, 3);
      })
    );
  }

  // ----------------- Acciones items -----------------
  deleteVademecum(id_vademecum: number) {
    this.vademecumsOrdenComercio.update(list =>
      list.map(v => {
        if (v.id_vademecum !== id_vademecum) return v;
        if (!v.b_activo) return v;

        const qty = v.n_cantidad ?? 1;

        if (qty > 1) {
          return { ...v, n_cantidad: qty - 1 };
        }
        return { ...v, b_activo: false, n_cantidad: 1 };
      })
    );

    this.refreshTotal();
  }

  restoreVademecum(id_vademecum: number) {
    this.vademecumsOrdenComercio.update(list =>
      list.map(v => {
        if (v.id_vademecum !== id_vademecum) return v;
        return { ...v, b_activo: true, n_cantidad: Math.max(1, v.n_cantidad ?? 1) };
      })
    );

    this.refreshTotal();
  }

  updateVademecum(id_vademecum: number, value: string | number): void {
    const n = Number(value);
    const list = [...this.vademecumsOrdenComercio()];
    const item = list.find(x => x.id_vademecum === id_vademecum);
    if (!item) return;

    item.n_precio_final = Number.isFinite(n) ? n : item.n_precio_final;
    this.vademecumsOrdenComercio.set(list);
    this.refreshTotal();
  }

  addVademecumCart(v: Vademecum): void {
    const list = [...this.vademecumsOrdenComercio()];
    const existing = list.find(x => x.id_vademecum === v.id);

    if (!existing) {
      const base = this.tempVademecumOrderComercio();
      list.push({
        id_orden_comercio: this.ordenComercio()!.id!,
        id_vademecum: v.id,
        n_cantidad: base?.n_cantidad ?? 1,
        n_precio: v.n_precio!,
        n_precio_final: v.n_precio!,
        x_nombre: v.x_nombre!,
        x_presentacion: v.x_presentacion!,
        nombreLaboratorio: v.nombreLaboratorio!,
        b_activo: true,
      });

      // desactivo el “original”
      if (base) base.b_activo = false;
    } else {
      existing.n_cantidad = (existing.n_cantidad ?? 0) + 1;
    }

    this.vademecumsOrdenComercio.set(list);
    this.refreshTotal();

    this.searchTermVademecums$.next('***');
    this.searchVademecums = '';
    this.closeModal();
  }

  deleteProducto(id_producto: number) {
    this.productosOrdenComercio.update(list =>
      list.map(p => {
        if (p.id_producto !== id_producto) return p;
        if (!p.b_activo) return p;

        const qty = p.n_cantidad ?? 1;

        if (qty > 1) {
          return { ...p, n_cantidad: qty - 1 };
        }
        return { ...p, b_activo: false, n_cantidad: 1 };
      })
    );

    this.refreshTotal();
  }

  restoreProducto(id_producto: number) {
    this.productosOrdenComercio.update(list =>
      list.map(p => {
        if (p.id_producto !== id_producto) return p;
        return { ...p, b_activo: true, n_cantidad: Math.max(1, p.n_cantidad ?? 1) };
      })
    );

    this.refreshTotal();
  }

  updateProducto(id_producto: number, value: string | number): void {
    const n = Number(value);
    const list = [...this.productosOrdenComercio()];
    const item = list.find(x => x.id_producto === id_producto);
    if (!item) return;

    item.n_precio_final = Number.isFinite(n) ? n : item.n_precio_final;
    this.productosOrdenComercio.set(list);
    this.refreshTotal();
  }

  addProductoCart(p: Producto): void {
    const list = [...this.productosOrdenComercio()];
    const existing = list.find(x => x.id_producto === p.id);

    if (!existing) {
      const base = this.tempProductoOrderComercio();
      list.push({
        id_orden_comercio: this.ordenComercio()!.id!,
        id_producto: p.id,
        n_cantidad: base?.n_cantidad ?? 1,
        n_precio: p.n_precio!,
        n_precio_final: p.n_precio!,
        n_porcentaje_descuento: p.n_porcentaje_descuento!,
        x_nombre: p.x_nombre!,
        x_sku: p.x_sku!,
        x_img_producto: p.x_img_producto,
        b_activo: true,
      });

      if (base) base.b_activo = false;
    } else {
      existing.n_cantidad = (existing.n_cantidad ?? 0) + 1;
    }

    this.productosOrdenComercio.set(list);
    this.refreshTotal();

    this.searchTermProductos$.next('***');
    this.searchProductos = '';
    this.closeModal();
  }

  // ----------------- Totales -----------------
  private isActivo(v: any): boolean {
    return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
  }

  refreshTotal(): void {
    const vads = this.vademecumsOrdenComercio();
    const prods = this.productosOrdenComercio();

    const totalV = vads
      .filter(v => this.isActivo(v.b_activo))
      .reduce((acc, v) => acc + (Number(v.n_precio_final) || 0) * (Number(v.n_cantidad) || 0), 0);

    const totalP = prods
      .filter(p => this.isActivo(p.b_activo))
      .reduce((acc, p) => acc + (Number(p.n_precio_final) || 0) * (Number(p.n_cantidad) || 0), 0);

    // Si querés el ahorro sobre precio original: usá n_precio.
    // Si querés el ahorro sobre el precio final: usá n_precio_final (pero no es lo típico).
    const ahorroV = vads
      .filter(v => this.isActivo(v.b_activo) && (Number(v.n_porcentaje_descuento) || 0) > 0)
      .reduce((acc, v) => {
        const precio = Number(v.n_precio) || 0;
        const qty = Number(v.n_cantidad) || 0;
        const desc = Number(v.n_porcentaje_descuento) || 0;
        return acc + (precio * qty * desc / 100);
      }, 0);

    const ahorroP = prods
      .filter(p => this.isActivo(p.b_activo) && (Number(p.n_porcentaje_descuento) || 0) > 0)
      .reduce((acc, p) => {
        const precio = Number(p.n_precio) || 0;
        const qty = Number(p.n_cantidad) || 0;
        const desc = Number(p.n_porcentaje_descuento) || 0;
        return acc + (precio * qty * desc / 100);
      }, 0);

    let total = totalV + totalP;

    if (this.b_envio_domicilio() && (Number(this.n_precio_envio()) || 0) > 0) {
      total += Number(this.n_precio_envio()) || 0;
    }

    this.n_total.set(total);
    this.n_ahorro.set(ahorroV + ahorroP);
  }

  // ----------------- Validar precio envío -----------------
  // Validar entrada para permitir solo números y un punto decimal
  onPrecioInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    // Reemplazar todas las comas por puntos
    // Reemplazar caracteres no numéricos (excepto el punto decimal)
    el.value = el.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
    // Controlar que solo haya un punto decimal
    const parts = el.value.split('.');
    if (parts.length > 2) el.value = parts[0] + '.' + parts.slice(1).join('');
    // Actualizar el valor como número (o 0 si está vacío)
    this.n_precio_envio.set(parseFloat(el.value) || 0);
    this.refreshTotal();
  }

  formatPrecio(): void {
    const n = this.n_precio_envio();
    // Asegurarse de que el número tenga dos decimales
    this.n_precio_envio.set(n ? parseFloat(n.toFixed(2)) : 0);
    this.refreshTotal();
  }

  // ----------------- Enviar respuesta -----------------
  async finalizaResponseOrder(): Promise<void> {
    this.error.set('');
    this.loading.set(true);

    const oc = this.ordenComercio();
    if (!oc) {
      this.error.set('No se pudo cargar la orden.');
      this.loading.set(false);
      return;
    }

    // notas
    if ((this.x_notas() ?? '').trim().length > 0) oc.x_notas = this.x_notas().trim();

    // envío
    if (oc.b_envio_domicilio) {
      if ((this.n_precio_envio() ?? 0) <= 0) {
        this.error.set('Debe completar el costo de envío');
        this.loading.set(false);
        return;
      }
      if ((this.n_tiempo_envio() ?? 0) <= 0) {
        this.error.set('Debe completar el tiempo de envío');
        this.loading.set(false);
        return;
      }
      oc.n_precio_envio = this.n_precio_envio();
      oc.n_tiempo_envio = this.n_tiempo_envio();
    }

    // productos
    const vadsActivos = this.vademecumsOrdenComercio().filter(x => x.b_activo);
    const prodsActivos = this.productosOrdenComercio().filter(x => x.b_activo);
    if (vadsActivos.length === 0 && prodsActivos.length === 0) {
      this.error.set('No puede responder el pedido porque no tiene productos');
      this.loading.set(false);
      return;
    }

    // estado respondido
    oc.id_estado_orden = 2; // RESPONDIDO

    await this._loadingService.run(async () => {
      await new Promise<void>((resolve, reject) => {
        this._orderComercioService
          .updateOrdenComercio(oc, this.vademecumsOrdenComercio(), this.productosOrdenComercio())
          .subscribe({
            next: () => resolve(),
            error: (e) => reject(e),
          });
      });

      this._orderComercioService.clearData();
      this.isFinished.set(true);
    }).catch((e) => {
      console.error(e);
      this.error.set('Ups, ha ocurrido un error al enviar la cotización.');
    }).finally(() => {
      this.loading.set(false);
    });
  }

  openFile(): void {
    const oc = this.ordenComercio();
    if (!oc?.x_img_receta) return;
    window.open(`${API_URI}uploads/images/${oc.x_img_receta}`, '_blank');
  }

  back(): void {
    this._location.back();
  }
}